import React, { useState } from 'react';
import { Save, X } from 'lucide-react';
import type { User } from '../../types/user';

interface UserFormProps {
  initialData?: User;
  onSubmit: (data: User | Omit<User, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onCancel: () => void;
}

const UserForm: React.FC<UserFormProps> = ({
  initialData,
  onSubmit,
  onCancel
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const formData = new FormData(e.target as HTMLFormElement);
      
      const data = {
        ...(initialData?.id ? { id: initialData.id } : {}),
        name: formData.get('name') as string,
        email: formData.get('email') as string,
        role: formData.get('role') as User['role'],
        status: formData.get('status') as User['status'],
        profileId: formData.get('profileId') as string,
        color: formData.get('color') as string,
        mustChangePassword: formData.get('mustChangePassword') === 'true',
        ...(initialData?.id
          ? {
              createdAt: initialData.createdAt,
              updatedAt: new Date()
            }
          : {})
      };

      await onSubmit(data as User);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="flex justify-between items-center p-6 border-b">
        <h2 className="text-xl font-semibold">
          {initialData ? 'Editar Usuário' : 'Novo Usuário'}
        </h2>
        <button
          onClick={onCancel}
          className="p-2 hover:bg-gray-100 rounded-full"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="p-6">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Nome Completo
            </label>
            <input
              type="text"
              name="name"
              defaultValue={initialData?.name}
              required
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              E-mail
            </label>
            <input
              type="email"
              name="email"
              defaultValue={initialData?.email}
              required
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Cargo
            </label>
            <select
              name="role"
              defaultValue={initialData?.role || 'seller'}
              required
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="admin">Administrador</option>
              <option value="manager">Gerente</option>
              <option value="financial">Financeiro</option>
              <option value="seller">Vendedor</option>
              <option value="stock">Estoque</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Status
            </label>
            <select
              name="status"
              defaultValue={initialData?.status || 'active'}
              required
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="active">Ativo</option>
              <option value="inactive">Inativo</option>
              <option value="blocked">Bloqueado</option>
              <option value="pending">Pendente</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Perfil de Acesso
            </label>
            <select
              name="profileId"
              defaultValue={initialData?.profileId || '1'}
              required
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="1">Acesso Total</option>
              <option value="2">Vendas</option>
              <option value="3">Financeiro</option>
              <option value="4">Estoque</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Cor de Identificação
            </label>
            <input
              type="color"
              name="color"
              defaultValue={initialData?.color || '#6B7280'}
              className="mt-1 block w-full h-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {!initialData && (
            <div className="col-span-2">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="mustChangePassword"
                  defaultChecked={true}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">
                  Exigir troca de senha no primeiro acesso
                </label>
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end space-x-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
          >
            <Save className="h-5 w-5 mr-2" />
            {isLoading ? 'Salvando...' : initialData ? 'Salvar Alterações' : 'Criar Usuário'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UserForm